import { create } from "zustand"

export type SessionSyncHealthWsStatus = "connecting" | "connected" | "disconnected" | "error"
export type SessionSyncHealthTransportMode = "websocket"

export interface SessionSyncHealthEntry {
  sessionId: string
  wsStatus: SessionSyncHealthWsStatus
  transportMode: SessionSyncHealthTransportMode
  wsStatusChangedAt: number
  degradedSinceAt: number | null
  lastUpdatedAt: number
}

interface SessionSyncHealthState {
  entries: Record<string, SessionSyncHealthEntry>
  upsertEntry: (
    sessionId: string,
    next: Omit<SessionSyncHealthEntry, "sessionId" | "lastUpdatedAt">,
  ) => void
  clearEntry: (sessionId: string) => void
}

export function isSessionSyncHealthUnhealthy(entry: SessionSyncHealthEntry | undefined): boolean {
  if (!entry) return false
  return entry.wsStatus !== "connected"
}

export const useSessionSyncHealth = create<SessionSyncHealthState>((set) => ({
  entries: {},
  upsertEntry: (sessionId, next) => set((state) => ({
    entries: {
      ...state.entries,
      [sessionId]: {
        sessionId,
        ...next,
        lastUpdatedAt: Date.now(),
      },
    },
  })),
  clearEntry: (sessionId) => set((state) => {
    if (!state.entries[sessionId]) return state
    const nextEntries = { ...state.entries }
    delete nextEntries[sessionId]
    return { entries: nextEntries }
  }),
}))

export function upsertSessionSyncHealthEntry(
  sessionId: string,
  next: Omit<SessionSyncHealthEntry, "sessionId" | "lastUpdatedAt">,
): void {
  useSessionSyncHealth.getState().upsertEntry(sessionId, next)
}

export function clearSessionSyncHealthEntry(sessionId: string): void {
  useSessionSyncHealth.getState().clearEntry(sessionId)
}

export function getSessionSyncHealthEntry(sessionId: string): SessionSyncHealthEntry | undefined {
  return useSessionSyncHealth.getState().entries[sessionId]
}
